import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Map, MailWarning, Sparkles, CalendarClock, ShieldCheck, Landmark } from "lucide-react";
import { api, errorMessage, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const statusCopy = {
  none: "No subscription", trial: "Trial active", active: "Premium active", paused: "Billing paused (Free Mode)",
  cancelled: "Cancelled — access until period end", lapsed: "Lapsed — downgraded to Free",
};

export default function Dashboard() {
  const { user, settings, refreshUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [devLink, setDevLink] = useState(sessionStorage.getItem("cgland_dev_verify_link"));
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data.dev_verification_link) { setDevLink(data.dev_verification_link); sessionStorage.setItem("cgland_dev_verify_link", data.dev_verification_link); }
      toast.success(data.message);
    } catch (err) { toast.error(errorMessage(err)); } finally { setSending(false); }
  };

  const cancel = async () => {
    try { await api.post("/subscriptions/cancel"); await refreshUser(); toast.success("Subscription cancelled. Access continues until the period ends."); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  const sub = user.subscription || {};
  const freeMode = settings?.site_mode === "free";
  const premium = user.effective_tier === "premium";
  const periodEnd = sub.status === "trial" ? sub.trial_ends_at : sub.current_period_end;

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 py-12">
      <p className="data-label mb-3">Dashboard</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Namaste, {user.name.split(" ")[0]}.</h1>

      {!user.email_verified && (
        <div data-testid="verify-email-banner" className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
          <MailWarning className="text-amber-700 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Verify your email to unlock subscriptions</p>
            <p className="text-amber-800/80 dark:text-amber-200/70">We sent a link to {user.email}.{devLink && <> Dev mode: <a data-testid="dev-verify-link" href={devLink} className="underline font-medium">open verification link</a></>}</p>
          </div>
          <Button data-testid="resend-verification-button" variant="outline" size="sm" className="rounded-full" onClick={resend} disabled={sending}>Resend</Button>
        </div>
      )}

      <div className="mt-10 grid lg:grid-cols-12 gap-6">
        <div data-testid="subscription-status-card" className={`lg:col-span-7 rounded-2xl p-8 relative overflow-hidden ${premium || freeMode ? "topo-bg grain text-white" : "border bg-card"}`}>
          <div className="relative z-10">
            <div className="flex items-center justify-between gap-4">
              <p className={`data-label ${premium || freeMode ? "text-emerald-400" : ""}`}>Your plan</p>
              <Badge data-testid="subscription-status-badge" className={premium || freeMode ? "bg-emerald-500 text-slate-950" : "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-100"}>
                {freeMode ? "Free Mode" : user.role === "superadmin" ? "Superadmin" : user.admin_override ? `Override: ${user.admin_override}` : sub.status === "cancelled" && premium ? "Cancelled" : premium ? "Premium" : "Free"}
              </Badge>
            </div>
            <h2 className="mt-4 text-2xl sm:text-3xl font-bold">{freeMode ? "Everything is unlocked right now" : premium ? "Premium maps unlocked" : "Free account"}</h2>
            <p className={`mt-2 ${premium || freeMode ? "text-slate-300" : "text-muted-foreground"}`}>
              {freeMode ? "The superadmin has enabled Free Mode. Maps are open to everyone and paid billing is paused." : statusCopy[sub.status] || statusCopy.none}
            </p>
            <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
              <div className="rounded-xl border border-white/10 bg-white/5 dark:bg-white/5 p-4" style={!premium && !freeMode ? { background: "hsl(var(--muted))", borderColor: "hsl(var(--border))" } : {}}>
                <p className="text-xs opacity-70 flex items-center gap-1"><CalendarClock size={12} /> {sub.status === "trial" ? "Trial ends" : "Renews / ends"}</p>
                <p data-testid="subscription-period-end" className="font-mono font-semibold mt-1">{formatDate(periodEnd)}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4" style={!premium && !freeMode ? { background: "hsl(var(--muted))", borderColor: "hsl(var(--border))" } : {}}>
                <p className="text-xs opacity-70">Payment method</p>
                <p className="font-mono font-semibold mt-1">{sub.card_last4 ? `•••• ${sub.card_last4}` : "—"}</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/5 p-4" style={!premium && !freeMode ? { background: "hsl(var(--muted))", borderColor: "hsl(var(--border))" } : {}}>
                <p className="text-xs opacity-70">Trial used</p>
                <p className="font-mono font-semibold mt-1">{user.trial_used ? "Yes" : "No — 7 days available"}</p>
              </div>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="dashboard-open-map-button" className={`rounded-full ${premium || freeMode ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-emerald-900 hover:bg-emerald-800 dark:bg-emerald-500 dark:text-slate-950"}`}>
                <Link to="/map"><Map size={16} className="mr-2" />Open maps</Link>
              </Button>
              {!premium && !freeMode && user.role !== "superadmin" && (
                <Button asChild data-testid="dashboard-upgrade-button" variant="outline" className="rounded-full" style={{ borderColor: "var(--terracotta)", color: "var(--terracotta)" }}>
                  <Link to="/pricing"><Sparkles size={16} className="mr-2" />Upgrade to premium · ₹{settings?.price_inr ?? 499}</Link>
                </Button>
              )}
              {["trial", "active", "paused"].includes(sub.status) && (
                <Button data-testid="dashboard-cancel-subscription-button" variant="ghost" className="rounded-full text-white/80 hover:text-white hover:bg-white/10" onClick={cancel}>Cancel subscription</Button>
              )}
            </div>
          </div>
        </div>

        <div className="lg:col-span-5 rounded-2xl border bg-card p-8">
          <p className="data-label">Public land data</p>
          <h3 className="mt-3 text-xl font-semibold flex items-center gap-2"><Landmark size={18} /> District coverage</h3>
          <p className="text-sm text-muted-foreground mt-1">Free for everyone. Parcel geometry requires premium.</p>
          <div data-testid="district-stats-list" className="mt-6 space-y-2 max-h-72 overflow-auto pr-1">
            {stats?.districts?.map((d) => (
              <div key={d.district} className="flex items-center justify-between rounded-xl bg-muted/60 px-4 py-2.5 text-sm">
                <span className="font-medium">{d.district}</span>
                <span className="font-mono text-xs text-muted-foreground">{d.parcels} parcels · {d.area_ha} ha</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck size={12} /> {stats?.total_parcels ?? "…"} parcels indexed statewide</p>
        </div>
      </div>
    </div>
  );
}
